import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';
import { usPhoneSchema } from '@/lib/validation/phone';

/** The four admin-side installation statuses (CONTEXT.md, ADR-0018). `completed` is a
 *  placeholder — not reachable until a field-installer flow exists. */
export const installationStatusSchema = z.enum(['open', 'in_progress', 'rejected', 'completed']);

export const assigneeTypeSchema = z.enum(['internal', 'external']);

/** A device an install was requested for (CONTEXT.md "Selected Device"). Real backend carries no
 *  id/image on the list row — just the name and coordinates for the map/tooltip. */
export const selectedDeviceSchema = z.object({
  deviceName: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

/** One row per order that has ≥1 device with an installation request. Keyed by `orderId` alone —
 *  the real backend (`InstallationRequestAdminListItem`) has no separate installation-request id,
 *  no customerId/customerAvatarUrl, and splits device count into total-vs-selected; `assignee` is
 *  a plain name with a sibling `assigneeType`, not a nested object. */
export const installationRowSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  phone: z.string(),
  orderDate: z.string(),
  totalDeviceCount: z.number(),
  selectedDeviceCount: z.number(),
  status: installationStatusSchema,
  assignee: z.string().nullable(),
  assigneeType: assigneeTypeSchema.nullable(),
  selectedDevices: z.array(selectedDeviceSchema),
});

export const installationListResponseSchema = z.object({
  data: z.array(installationRowSchema),
  meta: pageMetaSchema,
});

/** Filter-drawer draft validation (mirrors orderFiltersSchema): device count is whole & >=1,
 *  and both the device-count and order-date ranges must satisfy From <= To. */
export const installationFiltersSchema = z
  .object({
    statuses: z.array(installationStatusSchema),
    assigneeIds: z.array(z.string()),
    assigneeTypes: z.array(assigneeTypeSchema),
    deviceCountFrom: z.number().nullable(),
    deviceCountTo: z.number().nullable(),
    orderDateFrom: z.string().nullable(),
    orderDateTo: z.string().nullable(),
  })
  .superRefine((v, ctx) => {
    for (const key of ['deviceCountFrom', 'deviceCountTo'] as const) {
      const n = v[key];
      if (typeof n !== 'number') continue;
      if (n < 1) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'No of Devices must be at least 1' });
      }
    }
  })
  .superRefine((v, ctx) => {
    if (
      typeof v.deviceCountFrom === 'number' &&
      typeof v.deviceCountTo === 'number' &&
      v.deviceCountFrom > v.deviceCountTo
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['deviceCountTo'],
        message: 'From value should not be greater than To value',
      });
    }
    if (v.orderDateFrom && v.orderDateTo && v.orderDateFrom > v.orderDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['orderDateTo'],
        message: 'From date cannot be after To date',
      });
    }
  });

/** One device on the detail's device list.
 *
 *  `imageUrl` is the product thumbnail the Figma drawer card draws (ADO 119717, QA reopen). It is
 *  **`.nullish()`, not required**, because `InstallationRequestAdminDetailDevice` does not carry it
 *  yet — the order item behind each device already stores an `imageKey` that the Orders, Replacement
 *  and Warranty detail payloads all presign into an `imageUrl`, so this is the same field arriving
 *  late rather than a new concept. Optional means a deployment predating it still parses and the
 *  card simply renders its placeholder, the `replacedCount` precedent in `orders/schemas.ts`. */
export const installationDetailDeviceSchema = z.object({
  id: z.string(),
  deviceName: z.string(),
  orderItemName: z.string(),
  sequence: z.number(),
  latitude: z.number(),
  longitude: z.number(),
  imageUrl: z.string().nullish(),
});

export const installationAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
});

/** Full installation-request detail (`InstallationRequestAdminDetailResponse`). Structured
 *  address, singular `assignee`/`assigneeType`, and `devices` replace the row's `selectedDevices`
 *  with the richer per-device id/orderItemName/sequence. */
export const installationDetailSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  phone: z.string(),
  address: installationAddressSchema,
  orderDate: z.string(),
  status: installationStatusSchema,
  rejectionReason: z.string().nullable(),
  assignee: z.string().nullable(),
  assigneeType: assigneeTypeSchema.nullable(),
  installationOn: z.string().nullable(),
  installationFeeAmount: z.number(),
  devices: z.array(installationDetailDeviceSchema),
});

/** Reject-modal form validation: reason is required (>=3 non-whitespace chars) and capped
 *  at 2000 chars on the wire. */
export const rejectInstallationSchema = z.object({
  reason: z
    .string()
    .max(2000, 'Reason must be at most 2000 characters')
    .refine((s) => s.trim().length >= 3, { message: 'Reason must be at least 3 characters' }),
});

/** Shared active-field-installer directory shape (`FieldInstallerListItem`): flat, unpaginated,
 *  first/last name separately (not a combined `name`). Serves both the filter drawer's Assignee
 *  options and the approve-modal typeahead. */
export const installerOptionSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

/** Approve-modal (internal path) form validation: pick a seeded installer + a date. */
export const approveInternalSchema = z.object({
  installerId: z.string().min(1, 'Choose an installer'),
  installationOn: z.string().min(1, 'Choose a date'),
});

/** Approve-modal (external path) form validation: free-text installer name/contact + date.
 *  The contact message is quoted verbatim from 112408's AC, hence the trailing full stop and the
 *  absence of "US" — it deliberately differs from the `Enter a valid 10-digit US phone number`
 *  wording the auth/customers/admin-users schemas share. Not drift. */
export const approveExternalSchema = z.object({
  employeeName: z.string().regex(/^[A-Za-z ]{1,50}$/, 'Enter 1–50 letters'),
  // The shared NANP rule (ADR-0035), matching the backend's `@IsUsPhone` on this field.
  contactNumber: usPhoneSchema('Enter a valid 10-digit phone number.'),
  installationOn: z.string().min(1, 'Choose a date'),
});
